import { Injectable, Injector, inject } from '@angular/core';
import { type FrameCreate } from '@bizzdesign/ai-assistant/shared';
import { SessionService } from '@bizzdesign/auth';
import { ToolType } from '@bizzdesign/bizzdesign-sdk';
import {
  type UUID,
  assertIsUUID,
  deriveUUID,
  generateUUID,
} from '@bizzdesign/bizzdesign-sdk/uuid';
import {
  type AdhocFrame,
  type AdhocFrameBase,
  type Frame,
  type FrameBase,
  type ViewNotFoundError,
  type WorkspaceBase,
  type WorkspaceFrameBase,
  type WorkspaceFrameItem,
  type WorkspaceItem,
  isWorkspaceItem,
  viewNotFoundErrorFactory,
} from '@bizzdesign/data-access-workspaces';
import { assertIsSystemAttributesUser } from '@bizzdesign/sdk-knowledgegraph';
import {
  type ElementCollection,
  type SystemAttributesUser,
  type TemplateType,
  type ToolInputsFactory,
  isElementQuery,
} from '@bizzdesign/shared/models';
import {
  VISUALIZATION_TEMPLATE_REGISTER,
  type VisualizationTemplateRegister,
  isPopulation,
} from '@bizzdesign/shared/utils';
import {
  EMPTY,
  type Observable,
  concatMap,
  filter,
  from,
  map,
  mergeMap,
  of,
  switchMap,
} from 'rxjs';
import {
  TOOL_SPEC_REGISTER,
  type ToolSpecRegister,
} from '../tokens/tool-spec-register.token';

type CreateWorkspaceFrameBaseOptions = {
  readonly name?: string;
  readonly frameId?: UUID;
};

@Injectable()
export class FrameFactoryService {
  private readonly injector = inject(Injector);
  private readonly sessionService = inject(SessionService);
  private readonly toolSpecRegister =
    inject<ToolSpecRegister>(TOOL_SPEC_REGISTER);
  private readonly templateRegister = inject<VisualizationTemplateRegister>(
    VISUALIZATION_TEMPLATE_REGISTER,
  );

  private toSystemAttributesUser(user: unknown): SystemAttributesUser {
    assertIsSystemAttributesUser(user);
    return {
      userId: user.userId,
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
    };
  }

  // Preserve the real item fields so the optimistic frame passes isWorkspaceItem.
  private preserveWorkspaceDetails(workspace: WorkspaceBase): WorkspaceBase {
    if (!isWorkspaceItem(workspace)) {
      return {
        id: workspace.id,
        name: workspace.name,
        archived: workspace.archived,
        users: workspace.users,
      };
    }
    const item: WorkspaceItem = {
      id: workspace.id,
      name: workspace.name,
      archived: workspace.archived,
      users: workspace.users,
      createdAt: workspace.createdAt,
      editedAt: workspace.editedAt,
      createdBy: workspace.createdBy,
      updatedBy: workspace.updatedBy,
    };
    return item;
  }

  private getToolTypeFromTemplateType(templateType: TemplateType): ToolType {
    return this.templateRegister[templateType]?.renderer === 'highcharts'
      ? ToolType.INSIGHT
      : ToolType.DESIGN;
  }

  private async getToolInputsFactory(
    toolType: ToolType,
  ): Promise<ToolInputsFactory> {
    return this.injector.get<ToolInputsFactory>(
      await this.toolSpecRegister[toolType]?.inputsFactoryType(),
    );
  }

  createFrame(): Frame {
    return {
      id: generateUUID(),
      name: 'Untitled',
      content$: from(this.getToolInputsFactory(ToolType.DESIGN)).pipe(
        map((inputsFactory) =>
          inputsFactory.createEmptyContent?.(ToolType.DESIGN),
        ),
        filter((content) => content !== undefined),
      ),
      toolType: ToolType.DESIGN,
    };
  }

  /**
   * Builds an unsaved design frame belonging to the given workspace.
   *
   * @param workspace - Workspace the new frame is created under.
   * @param options - Optional frame name and stable id. When omitted, the frame
   * is called "Untitled" and a new id is generated.
   * @returns An observable of the frame, resolved once the authenticated user is
   * known so it can be stamped as the creator.
   */
  createWorkspaceFrameBase(
    workspace: WorkspaceBase,
    options: CreateWorkspaceFrameBaseOptions = {},
  ): Observable<WorkspaceFrameBase> {
    return this.sessionService.getAuthenticatedUser().pipe(
      map((user) => {
        const createdBy = this.toSystemAttributesUser(user);
        return {
          id: options.frameId ?? generateUUID(),
          name: options.name ?? 'Untitled',
          content$: from(this.getToolInputsFactory(ToolType.DESIGN)).pipe(
            map((inputsFactory) =>
              inputsFactory.createEmptyContent?.(ToolType.DESIGN),
            ),
            filter((content) => content !== undefined),
          ),
          populatedBy: undefined,
          sourceQuery: undefined,
          viewItem: undefined,
          toolType: ToolType.DESIGN,
          pinned: false,
          workspace: this.preserveWorkspaceDetails(workspace),
          createdBy,
        };
      }),
    );
  }

  createWorkspaceFrameForView(
    viewId: string,
    frame: WorkspaceFrameItem,
  ): Observable<WorkspaceFrameBase | ViewNotFoundError> {
    assertIsUUID(viewId);
    return from(this.getToolInputsFactory(ToolType.DESIGN)).pipe(
      concatMap(
        (inputsFactory) =>
          inputsFactory.createContentForView?.(
            viewId,
            { convertToFreeFormat: true },
            ToolType.DESIGN,
          ) ?? EMPTY,
      ),
      filter((result) => result !== undefined),
      concatMap(
        (result): Observable<WorkspaceFrameBase | ViewNotFoundError> =>
          result instanceof Error
            ? of(viewNotFoundErrorFactory(viewId))
            : this.sessionService.getAuthenticatedUser().pipe(
                mergeMap(async (user) => {
                  const createdBy = this.toSystemAttributesUser(user);
                  return {
                    id: frame.id,
                    name: frame.name,
                    content$: of(result.content),
                    populatedBy: undefined,
                    sourceQuery: undefined,
                    viewItem: {
                      id: await deriveUUID(viewId),
                      name: result.name,
                      viewId,
                    },
                    toolType: ToolType.DESIGN,
                    pinned: false,
                    workspace: frame.workspace,
                    createdBy,
                  };
                }),
              ),
      ),
    );
  }

  createWorkspaceFrameItem(
    workspace: WorkspaceBase,
    templateType: TemplateType,
  ): Observable<WorkspaceFrameItem> {
    return this.sessionService.getAuthenticatedUser().pipe(
      map((user) => {
        const createdBy = this.toSystemAttributesUser(user);
        return {
          id: generateUUID(),
          name: 'Untitled',
          pinned: false,
          toolType: this.getToolTypeFromTemplateType(templateType),
          workspace: this.preserveWorkspaceDetails(workspace),
          createdBy,
        };
      }),
    );
  }

  duplicateWorkspaceFrameItem(
    sourceFrame: WorkspaceFrameItem,
  ): Observable<WorkspaceFrameItem> {
    return this.sessionService.getAuthenticatedUser().pipe(
      map((user) => {
        const createdBy = this.toSystemAttributesUser(user);
        return {
          id: generateUUID(),
          name: `Copy of ${sourceFrame.name}`,
          pinned: false,
          workspace: sourceFrame.workspace,
          toolType: sourceFrame.toolType,
          createdBy,
        };
      }),
    );
  }

  createFrameForView(viewId: string): Observable<Frame | ViewNotFoundError> {
    assertIsUUID(viewId);
    return from(this.getToolInputsFactory(ToolType.DESIGN)).pipe(
      concatMap(
        (inputsFactory) =>
          inputsFactory.createContentForView?.(
            viewId,
            undefined,
            ToolType.DESIGN,
          ) ?? EMPTY,
      ),
      filter((content) => content !== undefined),
      map((result): Frame | ViewNotFoundError =>
        result instanceof Error
          ? viewNotFoundErrorFactory(viewId)
          : {
              id: viewId,
              name: result.name,
              content$: of(result.content),
              toolType: ToolType.DESIGN,
            },
      ),
    );
  }

  createFrameBaseForView(viewId: string, viewName: string): FrameBase {
    assertIsUUID(viewId);
    return { id: viewId, name: viewName };
  }

  createAdhocFrameForConfiguration(
    frame: AdhocFrameBase,
    configuration: object,
    dataset: UUID[],
    templateType: TemplateType,
  ): AdhocFrame {
    const toolType = this.getToolTypeFromTemplateType(templateType);
    return {
      ...frame,
      content$: from(this.getToolInputsFactory(toolType)).pipe(
        map((inputsFactory) =>
          inputsFactory.createContentForConfiguration(
            configuration,
            dataset,
            toolType,
          ),
        ),
      ),
      toolType: toolType,
    };
  }

  createAdhocFrameBase(id: string, name: string): AdhocFrameBase {
    assertIsUUID(id);
    return { id, name, isAdhoc: true };
  }

  createWorkspaceFrameForConfiguration(
    name: string,
    configuration: object,
    dataset: UUID[],
    frame: WorkspaceFrameItem,
  ): Observable<WorkspaceFrameBase> {
    const toolType = frame.toolType;
    return this.sessionService.getAuthenticatedUser().pipe(
      map((user) => {
        const createdBy = this.toSystemAttributesUser(user);
        return {
          id: frame.id,
          name: name,
          content$: from(this.getToolInputsFactory(toolType)).pipe(
            map((inputsFactory) =>
              inputsFactory.createContentForConfiguration(
                configuration,
                dataset,
                toolType,
              ),
            ),
          ),
          workspace: frame.workspace,
          populatedBy: undefined,
          sourceQuery: undefined,
          viewItem: undefined,
          toolType: toolType,
          pinned: frame.pinned,
          createdBy,
        };
      }),
    );
  }

  createWorkspaceFrameForElementCollection(
    collection: ElementCollection,
    frame: WorkspaceFrameItem,
    templateType: TemplateType,
  ): Observable<WorkspaceFrameBase> {
    const toolType = this.getToolTypeFromTemplateType(templateType);
    return this.sessionService.getAuthenticatedUser().pipe(
      switchMap((user) => {
        const createdBy = this.toSystemAttributesUser(user);
        return from(this.getToolInputsFactory(toolType)).pipe(
          map((inputsFactory) => ({
            id: frame.id,
            name: frame.name,
            content$: inputsFactory.createContentForElementCollection(
              collection,
              templateType,
              toolType,
            ),
            workspace: frame.workspace,
            populatedBy: isPopulation(collection)
              ? inputsFactory.createPopulatedByForPopulation(
                  collection,
                  toolType,
                )
              : undefined,
            sourceQuery: isElementQuery(collection)
              ? inputsFactory.createSourceQueryForElementQuery(
                  collection,
                  toolType,
                )
              : undefined,
            viewItem: undefined,
            toolType,
            pinned: false,
            createdBy,
          })),
        );
      }),
    );
  }

  createFrameForFrameCreate(create: FrameCreate): Frame {
    const toolType = this.getToolTypeFromTemplateType(create.templateType);
    return {
      id: generateUUID(),
      name: create.name,
      content$: from(this.getToolInputsFactory(toolType)).pipe(
        concatMap((inputsFactory) => {
          if (create.elements) {
            return inputsFactory.createContentForElementCollection(
              create.elements,
              create.templateType,
              toolType,
            );
          } else if (create.diagramPayload) {
            return of(
              inputsFactory.createContentForContent(
                create.diagramPayload,
                toolType,
              ),
            );
          }
          return EMPTY;
        }),
      ),
      toolType: toolType,
      ...(create.preview !== undefined ? { preview$: of(create.preview) } : {}),
    };
  }
}
